import {
  createMemo,
  splitProps,
  mergeProps,
  untrack,
  createResource,
  createEffect,
  Show,
} from "https://cdn.jsdelivr.net/npm/solid-js@1.9.5/+esm";
import html from "https://cdn.jsdelivr.net/npm/solid-js@1.9.5/html/+esm";
import {
  customShadowlessElement,
  toHyphenated,
} from "https://cdn.jsdelivr.net/npm/@brytdesigns/web-component-utils@0.1.8/+esm";
import { useProduct } from "https://cdn.jsdelivr.net/npm/@brytdesigns/shopify-product-info-components@1.0.6/+esm";
import {
  createContext,
  provide,
  consume,
} from "https://cdn.jsdelivr.net/npm/component-register/+esm";

function createWidgetSettingsUrl(storeUrl) {
  return `https://subs.api.tryprive.com/stores/${storeUrl}/settings`;
}

function createWidgetProductSellingPlansUrl({ storeUrl, productId }) {
  return `https://subs.api.tryprive.com/stores/${storeUrl}/products/${productId}/new`;
}

async function fetchWidgetSettings(storeUrl) {
  const endpoint = createWidgetSettingsUrl(storeUrl);
  const response = await fetch(endpoint, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

async function fetchProductSellingPlans({ storeUrl, productId }) {
  const endpoint = createWidgetProductSellingPlansUrl({ storeUrl, productId });
  const response = await fetch(endpoint, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
}

async function fetchWidgetData({ storeUrl, productId }) {
  const [settings, options] = await Promise.all([
    fetchWidgetSettings(storeUrl),
    fetchProductSellingPlans({ storeUrl, productId }),
  ]);

  return { settings, options };
}

function CustomRadio(props) {
  return html`
    <div class="relative h-max">
      <span
        class="border border-current h-[1.25em] w-[1.25em] rounded bg-white block transition-colors group-peer-checked:bg-current"
      ></span>
      <span
        class="block absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 w-[.5em] h-[.5em] rounded-full bg-transparent scale-75 transition-all group-peer-checked:bg-white group-peer-checked:scale-100"
      ></span>
    </div>
  `;
}

function GroupOptionLabel(props) {
  const [component, rest] = splitProps(props, ["label", "children"]);
  return html`
    <label
      ...${rest}
      class="border rounded border-section-body p-3.5 block text-sm font-bold peer-checked:bg-section-accent-one transition-colors gap-2 group text-section-body cursor-pointer flex gap-2 font-[Poppins,_sans-serif]"
    >
      <${CustomRadio} />
      ${() => component.label}
    </label>
    ${() => component.children}
  `;
}

function extractPeriod(input) {
  const lower = input.toLowerCase();
  if (lower.includes("week")) return "WEEK";
  if (lower.includes("month")) return "MONTH";
  if (lower.includes("day") || lower.includes("daily")) return "DAY";
  return "DAY";
}

function getSelectedSellingPlanByVariant({ variant, sellingPlans }) {
  if (!variant) return null;
  if (!sellingPlans?.length) return null;
  const variantCadenceCount = parseInt(variant.title);
  const variantCadenceUnit = extractPeriod(variant.title);
  const sellingPlan = sellingPlans.find((plan) => {
    return (
      plan.deliveryCadenceCount === variantCadenceCount &&
      plan.deliveryCadenceUnit === variantCadenceUnit
    );
  });
  return sellingPlan;
}

function calcSellingPlanPrice({ variant, sellingPlan }) {
  const discount = sellingPlan?.discount;
  const price = variant.price;
  if (!discount || !sellingPlan) return price;
  let discountedPrice;
  if (sellingPlan?.discountModifier === "PERCENTAGE") {
    discountedPrice = price - price * (discount / 100);
  } else {
    discountedPrice = price - discount;
  }
  return discountedPrice;
}

const PriveWidgetContext = createContext((props) => {
  const [_internal, _state] = splitProps(props, ["root"]);
  const [state] = useProduct(_internal.root);
  const [data] = createResource(
    () => ({
      storeUrl: props.storeUrl,
      productId: state.product.id || props.productId,
    }),
    fetchWidgetData,
  );
  const settings = () => data()?.settings?.widget_settings;
  const sellingPlanGroups = () => data()?.options?.selling_plan_groups;
  const variant = () => state.product.selected_or_first_available_variant;

  function updatePurchaseOption(option) {
    const current = untrack(() => _state.purchaseOption);
    if (current === option) return;
    _internal.root.setAttribute(toHyphenated("purchaseOption"), option);
  }

  const sellingPlans = createMemo(() => {
    return sellingPlanGroups()?.flatMap((group) => group.selling_plans) ?? [];
  });

  const selectedSellingPlan = createMemo(() => {
    const plans = sellingPlans();
    const selectedVariant = variant();
    return getSelectedSellingPlanByVariant({
      variant: selectedVariant,
      sellingPlans: plans,
    });
  });

  const sellingPlanAmount = createMemo(() => {
    const sellingPlan = selectedSellingPlan();
    return calcSellingPlanPrice({ variant: variant(), sellingPlan });
  });

  const sellingPlanDiscountPercentage = createMemo(() => {
    const sellingPlanPrice = sellingPlanAmount();
    const price = variant().price;
    return 100 - Math.round((sellingPlanPrice / price) * 100);
  });

  return [
    mergeProps(_state, {
      widget: {
        get data() {
          return data();
        },
        get settings() {
          return settings();
        },
        get sellingPlanGroups() {
          return sellingPlanGroups();
        },
      },
      sellingPlan: {
        get plans() {
          return sellingPlans();
        },
        get selected() {
          return selectedSellingPlan();
        },
        get price() {
          return sellingPlanAmount();
        },
        get discountPercentage() {
          return sellingPlanDiscountPercentage();
        },
      },
    }),
    {
      purchaseOption: {
        update: updatePurchaseOption,
      },
    },
  ];
});

export function usePriveWidget(element) {
  const context = consume(PriveWidgetContext, element);
  if (!context) {
    throw new Error(
      "No PriveWidgetContext found in the component tree, make sure to wrap your component in a prive-widget-context",
    );
  }
  return context;
}

customShadowlessElement(
  "prive-widget-context",
  {
    storeUrl: "",
    purchaseOption: "sub",
  },
  (props, { element }) => {
    if (!props.storeUrl)
      return console.warn("prive-widget: No store-url attributes provided");
    provide(PriveWidgetContext, mergeProps(props, { root: element }), element);
  },
);

customShadowlessElement(
  "prive-price-per-bite",
  {
    variantId: "",
  },
  (props, { element }) => {
    if (!props.variantId)
      return console.warn(
        "prive-price-per-bite: No variant-id attributes provided",
      );
    const [state, _] = usePriveWidget(element);
    const [productState] = useProduct(element);

    const variant = () =>
      productState.product.variants.find(
        (v) => `${v.id}` === `${props.variantId}`,
      );

    const pricePerBite = createMemo(() => {
      const v = variant();
      const period = extractPeriod(v.title);
      const amount = parseInt(v.title);
      const sellingPlans = state.sellingPlan.plans;

      const sellingPlan = getSelectedSellingPlanByVariant({
        variant: v,
        sellingPlans,
      });

      let price;

      if (sellingPlan && state.purchaseOption === "sub") {
        price = calcSellingPlanPrice({ sellingPlan, variant: v });
      } else {
        price = v.price;
      }

      let multiplier = 1;

      if (period === "WEEK") {
        multiplier = 7;
      } else if (period === "MONTH") {
        multiplier = 30;
      }

      if (isNaN(amount)) return 0;

      return price / (amount * multiplier);
    });

    const formattedPrice = createMemo(() =>
      Shopify.formatMoney(pricePerBite()),
    );

    return html`
      <${Show} when=${() => pricePerBite() > 0}>
        ${() => `${formattedPrice()}/Bite`}
      <//>
    `;
  },
);

customShadowlessElement(
  "prive-variant-widget",
  {
    form: "",
  },
  (props, { element }) => {
    const [productState] = useProduct(element);
    const [state, methods] = usePriveWidget(element);
    const variant = () =>
      productState.product.selected_or_first_available_variant;

    const firstSellingPlan = () =>
      state.widget.sellingPlanGroups?.at(0)?.selling_plans?.at(0);

    const formattedSellingPlanAmount = createMemo(() => {
      const sellingPlan = state.sellingPlan.selected;
      const discount = state.sellingPlan.discountPercentage;
      const price = variant().price;
      if (!discount || !sellingPlan) return Shopify.formatMoney(price);
      return Shopify.formatMoney(state.sellingPlan.price);
    });

    const otpAllowed = () =>
      state.widget.sellingPlanGroups?.some((group) =>
        group.type.includes("ONETIME"),
      );

    const hasSellingPlans = () => state.widget.sellingPlanGroups?.length > 0;

    const otpIsFirst = () => state.widget.settings?.onetimeFirst;

    const hasShippingDiscount = () =>
      !!state.sellingPlan.selected.shippingDiscount;

    const hasDiscount = () => !!state.sellingPlan.selected.discount;

    function OtpOption() {
      const id = () =>
        props.form
          ? `${props.form}-purchaseOption-${productState.product.id}-one`
          : `purchaseOption-${productState.product.id}-one`;

      const label = () => html`
        <span class="flex justify-between w-full">
          <span
            >${() =>
              state.widget.settings?.onetimeCopy || "One-time purchase:"}</span
          >
          <span>${() => Shopify.formatMoney(variant().price)}</span>
        </span>
      `;

      return html`
        <div>
          <input
            type="radio"
            form=${() => props.form || undefined}
            id=${id}
            name="purchase_option"
            class="sr-only peer"
            value="one"
            checked=${() => state.purchaseOption === "one"}
            onChange=${(e) => methods.purchaseOption.update("one")}
          />
          <${GroupOptionLabel} for=${id} label=${label} />
        </div>
      `;
    }

    function SellingPlanOption() {
      const id = () =>
        props.form
          ? `${props.form}-purchaseOption-${productState.product.id}-sub`
          : `purchaseOption-${productState.product.id}-sub`;

      const defaultCopy = () =>
        hasDiscount() ? "Subscribe and Save:" : "Subscribe:";

      function formatSellingPlanPrice() {
        return Shopify.formatMoney(
          firstSellingPlan()?.shippingDiscount.requirements.amount * 100,
        );
      }

      const label = () => html`
        <span class="flex justify-between w-full">
          <span
            >${() =>
              state.widget.settings?.subscribeCopy || defaultCopy()}</span
          >
          <span class="flex flex-col items-center relative">
            <${Show} when=${() => state.sellingPlan.discountPercentage > 0}>
              <span
                class="absolute -top-5 text-[10px] bg-section-accent-two rounded-full font-normal leading-none py-0.5 px-1 border border-current left-1/2 text-center min-w-14 -translate-x-1/2"
              >
                Save${" "}${() => state.sellingPlan.discountPercentage}%
              </span>
            <//>
            <span>${() => formattedSellingPlanAmount()}</span>
          </span>
        </span>
      `;

      const Label = () => html`
        <div class="flex flex-col w-full">
          ${label}
          <${Show} when=${() => hasShippingDiscount()}>
            <em class="text-xs font-normal">
              Free shipping on orders over ${" "}
              ${() => formatSellingPlanPrice()}
            </em>
          <//>
        </div>
      `;

      return html`
        <div>
          <input
            type="radio"
            form=${() => props.form || undefined}
            id=${id}
            name="purchase_option"
            class="sr-only peer"
            value="sub"
            checked=${() => state.purchaseOption === "sub"}
            onChange=${(e) => methods.purchaseOption.update("sub")}
          />
          <${GroupOptionLabel} for=${id} label=${Label} />
        </div>
      `;
    }

    const id = () =>
      props.form
        ? `${props.form}-${productState.product.id}-sellingPlan`
        : `purchaseOption-${productState.product.id}-sellingPlan`;

    return html`
      <${Show} when=${() => state.widget.data}>
        <${Show} when=${hasSellingPlans}>
          <${Show} when=${() => !!state.sellingPlan.selected}>
            <div class="flex flex-col gap-2">
              <${Show}
                when=${() =>
                  state.purchaseOption === "sub" &&
                  !!state.sellingPlan.selected}
              >
                <input
                  type="hidden"
                  form=${() => props.form || undefined}
                  id=${id}
                  name="selling_plan"
                  class="sr-only"
                  value=${() => state.sellingPlan.selected?.sellingPlanId}
                />
              <//>
              <${Show} when=${() => otpIsFirst() && otpAllowed()}>
                <${OtpOption} />
              <//>
              <${SellingPlanOption} />
              <${Show} when=${() => !otpIsFirst() && otpAllowed()}>
                <${OtpOption} />
              <//>
            </div>
          <//>
        <//>
      <//>
    `;
  },
);
